import { useState, useEffect, useCallback, useRef } from 'react';
import { user, admin } from 'src/backend';
import { useSnackbar } from 'notistack';
import { setSession } from 'src/redux/slices/auth';
import { PATH_DASHBOARD } from 'src/routes/paths';

export default function useTeams(options = {}) {
  const { autoLoad = true } = options;
  const { enqueueSnackbar } = useSnackbar();
  const [teams, setTeams] = useState();
  const [error, setError] = useState(null);
  const switchingRef = useRef(false);

  const getTeamsByUserId = useCallback(
    (userId) => {
      admin
        .getTeamsOfUser(userId)
        .then(setTeams)
        .catch(() =>
          enqueueSnackbar('There was an error fetching teams', {
            variant: 'error',
          })
        );
    },
    [enqueueSnackbar]
  );

  const refresh = useCallback((args) => {
    setError(null);
    user
      .getTeams({
        search: args?.search,
      })
      .then(setTeams)
      // Callers that care can show `error`; `loading` keeps its old meaning.
      .catch((err) => setError(err ?? new Error('Failed to load teams')));
  }, []);

  useEffect(() => {
    if (autoLoad) {
      refresh();
    }
  }, [autoLoad, refresh]);

  return {
    teams: teams ?? [],
    loading: !teams,
    error,
    refresh,
    switchTo: (teamId, { changeRoute = true, noReload = false } = {}) => {
      if (switchingRef.current) return Promise.resolve();
      switchingRef.current = true;

      const refreshToken = localStorage.getItem('refresh-token');

      return user
        .switchTeams(teamId, refreshToken)
        .then(({ token, refreshToken }) => {
          localStorage.removeItem('refresh-token'); // will no longer be valid after switching teams
          localStorage.removeItem('active-geofences');
          setSession(token, refreshToken);

          if (changeRoute) {
            window.location.pathname = PATH_DASHBOARD.root;
          }

          if (!noReload) {
            setTimeout(() => window.location.reload(), 500);
          }
        })
        .catch((err) =>
          enqueueSnackbar(
            err?.message || 'There was an error switching teams',
            {
              variant: 'error',
            }
          )
        )
        .finally(() => {
          switchingRef.current = false;
        });
    },
    getTeamsByUserId,
  };
}
